import { useState, useRef, useEffect, useId } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { springs } from "../motion";
import { Button } from "../primitives/Button";
import { Input } from "../primitives/Input";
import { TagColorPicker } from "./TagColorPicker";
import type { TagCardProps } from "./types";

export function TagCard({
  tag,
  mode,
  onStartEdit,
  onCancelEdit,
  onSave,
  onStartDelete,
  onCancelDelete,
  onConfirmDelete,
  animationDelay = 0,
}: TagCardProps) {
  const { t } = useTranslation(["common"]);
  const [editName, setEditName] = useState(tag.name);
  const [editColor, setEditColor] = useState(tag.color);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const nameInputId = useId();

  useEffect(() => {
    if (mode === "edit") {
      setEditName(tag.name);
      setEditColor(tag.color);
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [mode, tag.name, tag.color]);

  const handleSave = async () => {
    if (!editName.trim() || isSaving) return;
    setIsSaving(true);
    try {
      await onSave({ name: editName.trim(), color: editColor });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await onConfirmDelete();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onCancelEdit();
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      transition={{
        ...springs.snappy,
        delay: animationDelay,
        layout: springs.snappy,
      }}
    >
      {/* Row — view or delete-confirm */}
      <div
        className={`group flex items-center gap-3 px-4 py-2.5 border-b border-border transition-colors ${
          mode === "delete" ? "bg-red-500/5" : "hover:bg-card-hover/50"
        }`}
      >
        {/* Color dot */}
        <span
          className="h-3 w-3 rounded-full shrink-0"
          style={{ backgroundColor: tag.color }}
        />

        {/* Name */}
        <span className="text-[13px] font-medium text-primary flex-1 truncate">
          {tag.name}
        </span>

        {/* Actions — hover-visible in view, always visible in delete */}
        <AnimatePresence mode="wait" initial={false}>
          {mode === "view" && (
            <motion.div
              key="actions"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <button
                type="button"
                onClick={onStartEdit}
                className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-card-hover transition-colors"
                aria-label={t("ui.tag.editTag")}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={onStartDelete}
                className="p-1.5 rounded-md text-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                aria-label={t("ui.tag.deleteTag")}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          )}

          {mode === "delete" && (
            <motion.div
              key="delete-confirm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="flex items-center gap-1.5"
            >
              <span className="text-[12px] text-secondary mr-1">
                {t("tags.deleteConfirm")}
              </span>
              <Button variant="ghost" size="sm" onClick={onCancelDelete}>
                {t("buttons.cancel")}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDelete}
                loading={isDeleting}
              >
                {t("buttons.delete")}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Inline edit panel */}
      <AnimatePresence initial={false}>
        {mode === "edit" && (
          <motion.div
            key="edit-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springs.snappy}
            className="overflow-hidden border-b border-border bg-card-hover/30"
          >
            <div className="px-4 py-3 space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label
                    htmlFor={nameInputId}
                    className="text-[11px] font-medium text-muted uppercase tracking-wider mb-1 block"
                  >
                    {t("labels.name")}
                  </label>
                  <Input
                    id={nameInputId}
                    ref={inputRef}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("tags.tagName")}
                  />
                </div>
              </div>

              <TagColorPicker
                value={editColor}
                onChange={setEditColor}
                label={t("labels.color")}
              />

              <div className="flex items-center justify-end gap-1.5 pt-1">
                <Button variant="ghost" size="sm" onClick={onCancelEdit}>
                  {t("buttons.cancel")}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  loading={isSaving}
                  disabled={!editName.trim()}
                >
                  {t("buttons.save")}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
